import React, { useMemo } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { selectAuth, selectIsLoading } from '@selectors'
import * as localForage from 'localforage'

import {
  Button,
  Paragraph,
  Title,
  IconInline,
  ResponsiveContainer,
  HOT_PINK
} from '@omnilert/components'

import RoundedButton from '@components/rounded-button/rounded-button.component'

import { submitAlertAction } from '@redux/actions/safewalk'

import { CountdownCircleTimer } from 'react-countdown-circle-timer'

import {
  ATTEMPT_SUBMIT_ALERT_WITH_LOCATION,
  SAFEWALK_UPDATE_STATE,
  UPDATE_LOADING
} from '@actions/types'

import i18n from 'i18n-js'

import dayjs from 'dayjs'

import useStyles from './safewalk-start.style'

const SafeWalkStart = () => {
  const dispatch = useDispatch()

  const auth = useSelector(selectAuth)
  const loading = useSelector(selectIsLoading)
  const { activeDisplay, displayStates } = useSelector((state) => state.safewalk)

  const classes = useStyles()

  const safeWalkState = auth?.userConfig?.safe_walk

  const contacts = auth?.userConfig?.emergency_contacts
  const dateCreated = safeWalkState?.date_created
  const duration = safeWalkState?.duration
  
  const warningDuration = duration === 60000 ? 30 : 90

  const now = dayjs()
  const seconds = dateCreated?.seconds || 0

  const initialRemainingTime = useMemo(() => {
    const diff = now.diff(dayjs(seconds * 1000))
    const timeElapsed = dayjs.duration(diff).asMilliseconds()

    if (timeElapsed < 0) return 0

    return duration === 0 ? 0 : (duration - timeElapsed) / 1000
  }, [now, seconds, duration])

  const {
    bodyText,
    gradientId,
    primaryButtonColor,
    primaryButtonId,
    primaryButtonText,
    primaryButtonTextColor,
    primaryButtonVariant,
    secondaryButtonColor,
    secondaryButtonId,
    secondaryButtonText,
    secondaryButtonTextColor,
    showSecondaryButton,
    countdownColor,
    state: activeState
  } = { ...activeDisplay }

  if (!activeState) return null

  const handleOnUpdate = (total) => {
    if (total !== 0 && total === warningDuration && activeState === 'walk-activated') {
      dispatch({
        type: SAFEWALK_UPDATE_STATE,
        payload: {
          activeDisplay: displayStates.find(({ state }) => state === 'warning'),
          alarmTriggeredManually: false,
          nextState: 'warning'
        }
      })
    }

    if (total === 0 && activeState === 'warning') {
      dispatch({
        type: SAFEWALK_UPDATE_STATE,
        payload: {
          activeDisplay: displayStates.find(({ state }) => state === 'alarm-active'),
          alarmTriggeredManually: false,
          nextState: 'alarm-active'
        }
      })
    }
  }

  const handleOnClick = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    const { id: nextState } = e.target
    const restartStates = ['alarm-cancelled', 'walk-ended']
    const shouldReset = restartStates.some(state => state === activeState)

    dispatch({
      type: UPDATE_LOADING,
      payload: {
        loadingAction: 'set',
        meta: nextState
      }
    })
    if (shouldReset) {
      const activeWalkId = auth?.userConfig?.safe_walk?.current_active_walk_id
      const messageId = auth?.userConfig?.safe_walk?.lucy_message_id

      const data = {
        doc_id: activeWalkId,
        lucy_message_id: messageId,
        state: nextState
      }

      dispatch(submitAlertAction({ auth, data }))

      const watchPositionId = await localForage.getItem('watchPositionId')

      navigator.geolocation.clearWatch(watchPositionId)
    } else {
      dispatch({
        type: ATTEMPT_SUBMIT_ALERT_WITH_LOCATION,
        payload: {
          alarmTriggeredManually: true,
          contacts,
          nextState
        }
      })
    }
  }

  const isLoadingPrimary = loading.some(({ meta }) => meta === primaryButtonId)
  const isLoadingSecondary = loading.some(({ meta }) => meta === secondaryButtonId)

  const durationSeconds = duration / 1000

  const disableButton = isLoadingPrimary || isLoadingSecondary
  return (
    <ResponsiveContainer className={classes.responsive}>
      <CountdownCircleTimer
        colors={activeState === 'alarm-active' ? HOT_PINK : `url(#${gradientId})`}
        colorsTime={[durationSeconds, warningDuration, 1]}
        duration={durationSeconds}
        initialRemainingTime={initialRemainingTime}
        isPlaying
        onUpdate={handleOnUpdate}
        size={300}
      >
        {({ remainingTime }) => {
          const formatedTimeRemaining = dayjs.duration({ seconds: remainingTime }).add(0, 's').format('HH:mm:ss')
          return (
            <div className={classes.countdownCircleContent}>
              <Title
                className={classes.countdownText}
                color={countdownColor}
              >
                {formatedTimeRemaining}
              </Title>
              <IconInline
                className={classes.icon}
                name='alarm'
                size={30}
              />
            </div>
          )
        }}
      </CountdownCircleTimer>
      <div className={classes.buttonsContainer}>
        {primaryButtonVariant !== 'outlined' &&
          <>
            <RoundedButton
              backgroundColor={secondaryButtonColor}
              color={secondaryButtonTextColor}
              disabled={(disableButton || !showSecondaryButton)}
              id={secondaryButtonId}
              isLoading={isLoadingSecondary}
              onClick={handleOnClick}
              text={i18n.t(secondaryButtonText)}
            />
            <RoundedButton
              backgroundColor={primaryButtonColor}
              color={primaryButtonTextColor}
              disabled={disableButton}
              id={primaryButtonId}
              isLoading={isLoadingPrimary}
              onClick={handleOnClick}
              text={i18n.t(primaryButtonText)}
            />
          </>}
      </div>
      <div className={classes.outlinedButtonContainer}>
        {bodyText &&
          <Paragraph
            color='var(--tips__text)'
            size='md'
          >
            {i18n.t(bodyText)}
          </Paragraph>}

        {primaryButtonVariant === 'outlined' &&
          <Button
            backgroundColor={primaryButtonColor}
            className={classes.primaryOutlinedButton}
            fullWidth
            hoverBackgroundColor={primaryButtonColor}
            id={primaryButtonId}
            isLoading={isLoadingPrimary}
            onClick={handleOnClick}
            size='lg'
            title={i18n.t(primaryButtonText)}
            titleClassName={classes.timerButtonTitle}
            variant={primaryButtonVariant}
          />}
      </div>
    </ResponsiveContainer>
  )
}

export default SafeWalkStart
